// © Thibault Sabathier, 2026. Tous droits réservés.
// frontend/src/views/jobs/searches/SearchFormModal.js
//
// Modale de publication / modification d'une recherche. Elle prépare la saisie et affiche les
// erreurs renvoyées par le serveur (422 champ par champ) ; elle ne REPRODUIT aucune règle métier.

import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CButton,
  CForm,
  CFormInput,
  CFormTextarea,
  CFormCheck,
  CFormLabel,
  CFormFeedback,
  CAlert,
  CSpinner,
} from '@coreui/react'
import CountrySelect from '../../../components/CountrySelect'
import SlugSelect from './SlugSelect'
import { canonicalCountryName } from '../../../utils/countryCode'
import { CONTRACT_TYPE_VALUES, getContractTypeLabel } from '../../../utils/contractTypes'
import { JOB_FAMILY_VALUES, getJobFamilyLabel } from '../../../utils/jobFamilies'
import { SECTOR_VALUES, getSectorLabel } from '../../../utils/sectors'
import { createSearch, updateSearch } from '../../../utils/memberSearchesApi'
import {
  SEARCH_KINDS,
  emptyForm,
  formFromSearch,
  buildPayload,
  parseApiError,
  errorKeyFor,
  fieldErrorKeyFor,
} from '../../../utils/memberSearches'
import './searches.scss'

/** Périmètre du formulaire d'après l'annonce existante : 'network' ou 'unit' (campus / entité). */
const scopeOf = (search) => (search.scopeKind === 'network' ? 'network' : 'unit')

const FieldError = ({ code, t }) =>
  code ? (
    <CFormFeedback invalid style={{ display: 'block' }}>
      {t(`searches:${fieldErrorKeyFor(code)}`)}
    </CFormFeedback>
  ) : null

/**
 * @param {{visible: boolean, search: (object|null), config: (object|null), defaultKind?: string,
 *          onClose: Function, onSaved: Function}} props
 *   search = annonce à modifier ; null = création.
 */
const SearchFormModal = ({
  visible,
  search,
  config,
  defaultKind = 'opportunity_search',
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation()
  const isEdit = Boolean(search)
  const [form, setForm] = useState(() => emptyForm(defaultKind))
  const [fieldErrors, setFieldErrors] = useState({})
  const [message, setMessage] = useState(null)
  const [saving, setSaving] = useState(false)
  const savingRef = useRef(false) // verrou synchrone : un double clic ne publie pas deux fois
  const initialScopeRef = useRef('')

  const limits = (config && config.limits) || {}
  const scopeCfg = config && config.scope

  // Réinitialise à chaque ouverture (création vierge, ou édition pré-remplie).
  useEffect(() => {
    if (!visible) return
    setFieldErrors({})
    setMessage(null)
    if (search) {
      const base = formFromSearch(search)
      const scope = scopeOf(search)
      initialScopeRef.current = scope
      setForm({ ...base, country: base.country ? canonicalCountryName(base.country) : '', scope })
    } else {
      const scope = scopeCfg && scopeCfg.hasUnitChoice ? scopeCfg.default : ''
      initialScopeRef.current = scope
      setForm({ ...emptyForm(defaultKind), scope })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, search, defaultKind])

  const set = (name, value) => setForm((prev) => ({ ...prev, [name]: value }))

  const toggleContract = (slug) =>
    setForm((prev) => ({
      ...prev,
      contractTypes: prev.contractTypes.includes(slug)
        ? prev.contractTypes.filter((c) => c !== slug)
        : [...prev.contractTypes, slug],
    }))

  const submit = async (e) => {
    e.preventDefault()
    if (savingRef.current) return
    savingRef.current = true
    setSaving(true)
    setFieldErrors({})
    setMessage(null)
    try {
      // À l'édition, le périmètre n'est envoyé que s'il a changé : on ne rejoue pas une règle de
      // publication (élargissement, unité du membre) sur une annonce dont on n'a pas touché la portée.
      const toSend =
        isEdit && form.scope === initialScopeRef.current ? { ...form, scope: '' } : form
      const payload = buildPayload(toSend, { isEdit })
      const data = isEdit ? await updateSearch(search.id, payload) : await createSearch(payload)
      onSaved({
        search: data.search,
        suggestCareerStatus: data.suggestCareerStatus || null,
        isEdit,
      })
      onClose()
    } catch (err) {
      const { code, fields } = parseApiError(err)
      if (code === 'VALIDATION') setFieldErrors(fields)
      setMessage(t(`searches:${errorKeyFor(code)}`))
    } finally {
      savingRef.current = false
      setSaving(false)
    }
  }

  const opportunity = form.kind === 'opportunity_search'
  const showScope = Boolean(scopeCfg && scopeCfg.hasUnitChoice)
  const unitLabelKey =
    scopeCfg && scopeCfg.unitKind === 'entity' ? 'scope.unit_entity' : 'scope.unit_campus'

  return (
    <CModal
      visible={visible}
      onClose={saving ? undefined : onClose}
      size="lg"
      backdrop="static"
      scrollable
      alignment="center"
    >
      {/* `display: contents` : le <form> ne doit PAS s'intercaler dans la chaîne de défilement de la
          modale (en-tête / corps défilant / pied). Sans cela le corps déborde et le pied est coupé. */}
      <CForm onSubmit={submit} className="ms-form-modal">
        <CModalHeader closeButton={!saving}>
          <CModalTitle>
            {t(isEdit ? 'searches:form.title_edit' : 'searches:form.title_create')}
          </CModalTitle>
        </CModalHeader>
        <CModalBody>
          {message && <CAlert color="danger">{message}</CAlert>}

          {!isEdit && (
            <div className="mb-3">
              <CFormLabel>{t('searches:form.kind_choice')}</CFormLabel>
              <div className="ms-kind-choice">
                {SEARCH_KINDS.map((k) => (
                  <button
                    key={k}
                    type="button"
                    className={`ms-kind-option ${form.kind === k ? 'is-selected' : ''}`}
                    onClick={() => set('kind', k)}
                    aria-pressed={form.kind === k}
                  >
                    <strong>{t(`searches:kind.${k}.label`)}</strong>
                    <span className="ms-note">{t(`searches:kind.${k}.help`)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mb-3">
            <CFormLabel htmlFor="ms-title">{t('searches:form.title')}</CFormLabel>
            <CFormInput
              id="ms-title"
              value={form.title}
              maxLength={limits.title}
              placeholder={t('searches:form.title_placeholder')}
              invalid={Boolean(fieldErrors.title)}
              onChange={(e) => set('title', e.target.value)}
            />
            <FieldError code={fieldErrors.title} t={t} />
          </div>

          <div className="mb-3">
            <CFormLabel htmlFor="ms-description">{t('searches:form.description')}</CFormLabel>
            <CFormTextarea
              id="ms-description"
              rows={4}
              value={form.description}
              maxLength={limits.description}
              placeholder={t('searches:form.description_placeholder')}
              invalid={Boolean(fieldErrors.description)}
              onChange={(e) => set('description', e.target.value)}
            />
            {limits.description ? (
              <div className="ms-note text-end">
                {form.description.length} / {limits.description}
              </div>
            ) : null}
            <FieldError code={fieldErrors.description} t={t} />
          </div>

          {opportunity && (
            <div className="mb-3">
              <CFormLabel>{t('searches:form.contract_types')}</CFormLabel>
              <div className="ms-contracts">
                {CONTRACT_TYPE_VALUES.map((slug) => (
                  <CFormCheck
                    key={slug}
                    id={`ms-contract-${slug}`}
                    label={getContractTypeLabel(slug, t)}
                    checked={form.contractTypes.includes(slug)}
                    onChange={() => toggleContract(slug)}
                  />
                ))}
              </div>
              <FieldError code={fieldErrors.contractTypes} t={t} />
            </div>
          )}

          {!opportunity && (
            <div className="mb-3">
              <CFormLabel htmlFor="ms-target">
                {t('searches:form.target_company')}{' '}
                <span className="ms-note">({t('searches:form.optional')})</span>
              </CFormLabel>
              <CFormInput
                id="ms-target"
                value={form.targetCompanyName}
                maxLength={limits.targetCompanyName}
                invalid={Boolean(fieldErrors.targetCompanyName || fieldErrors.target)}
                onChange={(e) => set('targetCompanyName', e.target.value)}
              />
              <div className="ms-note">{t('searches:form.target_company_help')}</div>
              <FieldError code={fieldErrors.targetCompanyName} t={t} />
              <FieldError code={fieldErrors.target} t={t} />
            </div>
          )}

          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <CFormLabel htmlFor="ms-job-family">{t('searches:form.job_family')}</CFormLabel>
              <SlugSelect
                id="ms-job-family"
                value={form.jobFamily}
                onChange={(slug) => set('jobFamily', slug)}
                values={JOB_FAMILY_VALUES}
                getLabel={getJobFamilyLabel}
                invalid={Boolean(fieldErrors.jobFamily)}
              />
              <FieldError code={fieldErrors.jobFamily} t={t} />
            </div>
            <div className="col-md-6">
              <CFormLabel htmlFor="ms-sector">{t('searches:form.sector')}</CFormLabel>
              <SlugSelect
                id="ms-sector"
                value={form.sector}
                onChange={(slug) => set('sector', slug)}
                values={SECTOR_VALUES}
                getLabel={getSectorLabel}
                invalid={Boolean(fieldErrors.sector)}
              />
              <FieldError code={fieldErrors.sector} t={t} />
            </div>
            <div className="col-md-6">
              <CFormLabel>{t('searches:form.country')}</CFormLabel>
              <CountrySelect
                value={form.country}
                onChange={(e) => set('country', e.target.value)}
                placeholder={t('searches:form.choose')}
                invalid={Boolean(fieldErrors.country)}
              />
              <FieldError code={fieldErrors.country} t={t} />
            </div>
            <div className="col-md-6">
              <CFormLabel htmlFor="ms-city">{t('searches:form.city')}</CFormLabel>
              <CFormInput
                id="ms-city"
                value={form.city}
                maxLength={limits.city}
                invalid={Boolean(fieldErrors.city)}
                onChange={(e) => set('city', e.target.value)}
              />
              <FieldError code={fieldErrors.city} t={t} />
            </div>
          </div>

          <div className="mb-3">
            <CFormCheck
              id="ms-remote"
              label={t('searches:form.remote_ok')}
              checked={form.remoteOk}
              onChange={(e) => set('remoteOk', e.target.checked)}
            />
          </div>

          {opportunity && (
            <div className="row g-3 mb-3">
              <div className="col-md-5">
                <CFormLabel htmlFor="ms-available">
                  {t('searches:form.available_from')}{' '}
                  <span className="ms-note">({t('searches:form.optional')})</span>
                </CFormLabel>
                <CFormInput
                  id="ms-available"
                  type="date"
                  value={form.availableFrom}
                  invalid={Boolean(fieldErrors.availableFrom)}
                  onChange={(e) => set('availableFrom', e.target.value)}
                />
                <FieldError code={fieldErrors.availableFrom} t={t} />
              </div>
              <div className="col-md-7">
                <CFormLabel htmlFor="ms-cv">
                  {t('searches:form.cv_url')}{' '}
                  <span className="ms-note">({t('searches:form.optional')})</span>
                </CFormLabel>
                <CFormInput
                  id="ms-cv"
                  type="url"
                  inputMode="url"
                  value={form.cvUrl}
                  maxLength={limits.cvUrl}
                  placeholder="https://"
                  invalid={Boolean(fieldErrors.cvUrl)}
                  onChange={(e) => set('cvUrl', e.target.value)}
                />
                <div className="ms-note">{t('searches:form.cv_url_help')}</div>
                <FieldError code={fieldErrors.cvUrl} t={t} />
              </div>
            </div>
          )}

          {showScope && (
            <div className="mb-2">
              <CFormLabel>{t('searches:scope.label')}</CFormLabel>
              <CFormCheck
                type="radio"
                name="ms-scope"
                id="ms-scope-unit"
                label={t(`searches:${unitLabelKey}`)}
                checked={form.scope === 'unit'}
                onChange={() => set('scope', 'unit')}
              />
              {scopeCfg.allowWiden ? (
                <CFormCheck
                  type="radio"
                  name="ms-scope"
                  id="ms-scope-network"
                  label={t('searches:scope.network')}
                  checked={form.scope === 'network'}
                  onChange={() => set('scope', 'network')}
                />
              ) : (
                <div className="ms-note">{t('searches:scope.widen_forbidden')}</div>
              )}
              <FieldError code={fieldErrors.scope} t={t} />
            </div>
          )}

          <p className="ms-note mt-3 mb-0">{t('searches:visibility_note')}</p>
        </CModalBody>
        <CModalFooter>
          <CButton
            type="button"
            color="secondary"
            variant="outline"
            disabled={saving}
            onClick={onClose}
          >
            {t('searches:actions.cancel')}
          </CButton>
          <CButton type="submit" color="primary" disabled={saving}>
            {saving && <CSpinner size="sm" className="me-2" />}
            {t(isEdit ? 'searches:actions.save' : 'searches:actions.publish')}
          </CButton>
        </CModalFooter>
      </CForm>
    </CModal>
  )
}

export default SearchFormModal
